<template>
  <section class="driver-user-feedback">
    <header>Оценка водителя и отзыв</header>

    <div
      class="stars"
      @mouseover="showReasonCannotRate($event)"
      @mouseleave="onStarsLeave()"
    >
      <VButton
        v-for="star in 5"
        :key="star"
        theme="link"
        @mouseenter="onStarHover(star)"
        @click="toggleRate(star)"
        :disabled="localFeedback.getReasonCannotRate() !== null"
      >
        <VIcon
          class="star"
          :material="DriversService.getRateStarIcon(hoverRate ?? localFeedback.rate, star)"
        />
      </VButton>
    </div>

    <VTextarea
      v-model="localFeedback.comment"
      layout="vertical"
      :disabled="localFeedback.getReasonCannotComment() !== null"
      :disabled-reasons="localFeedback.getReasonCannotComment()"
    >
      <template
        v-if="localFeedback.getReasonCannotComment() === null"
        #helper-text
      >
        Чтобы сохранить оценку и отзыв нажмите кнопку "Сохранить" в нижней части окна
      </template>
    </VTextarea>
  </section>
</template>

<style lang="scss" src="./DriverUserFeedback.scss"/>

<script lang="ts">

import DriverFeedback from '@/domains/Drivers/DriverFeedback';
import DriversService from '@/domains/Drivers/DriversService';
import type { UnwrapNestedRefs } from 'vue';

/**
 * Своя оценка водителя и отзыв: звёзды с подсветкой при наведении и комментарий.
 * Правит переданную оценку на месте; изменилась ли она и когда её сохранять — решает родитель
 */
export default defineComponent({
  name: 'DriverUserFeedback',

  props: {
    /** Своя оценка водителя — существующая либо новая (DriverFeedback.fromDriver); null — водителя нет, блок задизейблен */
    feedback: {
      type:    DriverFeedback as PropType<DriverFeedback | UnwrapNestedRefs<DriverFeedback> | null>,
      default: null,
    },
  },

  setup() {
    return {
      DriversService,
    };
  },

  data() {
    return {
      /** Звезда под курсором — пока наводят, подсвечиваем оценку как при клике; null — курсор вне звёзд */
      hoverRate: null as number | null,
    };
  },

  computed: {
    /** Без водителя оценки нет — пустая только для отображения, контролы при этом задизейблены */
    localFeedback(): DriverFeedback | UnwrapNestedRefs<DriverFeedback> {
      if (this.$core.utils.isNullOrUndefined(this.feedback) === true) {
        return new DriverFeedback();
      }
      else {
        return this.feedback;
      }
    },
  },

  methods: {
    /**
     * Причина, почему оценить нельзя, — одна на все звёзды.
     * На mouseover, а не mouseenter: VButton на своём mouseleave прячет тултип,
     * и при переходе между звёздами его нужно показать снова
     */
    showReasonCannotRate(event: MouseEvent): void {
      const reason = this.localFeedback.getReasonCannotRate();

      if (reason !== null) {
        this.$core.tooltip.show(event.currentTarget, reason);
      }
      else {
        // оценивать можно — подсказка не нужна
      }
    },

    onStarsLeave(): void {
      this.hoverRate = null;
      this.$core.tooltip.hide();
    },

    onStarHover(star: number): void {
      if (this.localFeedback.getReasonCannotRate() === null) {
        this.hoverRate = star;
      }
      else {
        // оценивать нельзя — не подсвечиваем
      }
    },

    /**
     * Клик по звезде ставит оценку. Повторный клик по звезде текущей оценки сбрасывает её —
     * чтобы случайно поставленную оценку можно было убрать.
     * При сбросе гасим и подсветку наведения: иначе под курсором звёзды остаются горящими и сброса не видно
     */
    toggleRate(star: number): void {
      if (this.localFeedback.rate === star) {
        this.localFeedback.rate = null;
        this.hoverRate          = null;
      }
      else {
        this.localFeedback.rate = star;
      }
    },
  },
});
</script>
